import React, { useCallback, useEffect, useState } from 'react';
import i18next from 'i18next';
import { SearchFilterNameTypes, SearchFilterTypes, SortOption } from '../../@types/Search';
import { Columns, Column } from '../../StyledComponents/Columns';
import { ButtonPrimary, ButtonSecondary } from '../../StyledComponents/Button';
import { defaultDisplaynameTranslation } from '../../utils/converter/facetNameConverter';
import FilterPanel from './FilterPanel';
import { Facet } from './SearchModels';
import SortPanel from './SortPanel';
import { isSelected } from './Utils/Filter';

const filterFacets = (facets: Facet[] | undefined, filterList?: SearchFilterTypes[]) => {
  const filterNames = filterList ? filterList.map((x) => x.filterName) : undefined;
  // The legacy API returned both `properties.segment` and a duplicate `segment`
  // facet; the duplicate is hidden. The public API has only `segment`.
  const hasLegacySegment = facets?.some((x) => x.id === 'properties.segment');
  return facets
    ?.filter((x) => !(x.id === 'segment' && hasLegacySegment))
    .filter((x) => x.filters.length)
    ?.filter((f) => (filterNames ? filterNames.includes(f.id as SearchFilterNameTypes) : true))
    .sort((a, b) =>
      filterNames
        ? filterNames.indexOf(a.id as SearchFilterNameTypes) -
          filterNames.indexOf(b.id as SearchFilterNameTypes)
        : 0
    )
    .map((x) => {
      const match = filterList?.find((y) => y.filterName === x.id);
      if (match?.displayName) {
        return {
          ...x,
          displayName: match.displayName,
        } as Facet;
      }
      return {
        ...x,
        displayName: defaultDisplaynameTranslation(x.id),
      } as Facet;
    });
};

interface SearchFiltersProps {
  urlSearchParams?: URLSearchParams;
  facets?: Facet[];
  initialFacets?: Facet[];
  loading?: boolean;
  totalHitsText: string;
  filterList?: SearchFilterTypes[];
  query?: string;
  sortOptions?: SortOption[];
  onFilterUpdate: (query: string) => void;
}

const SearchFilters = ({
  urlSearchParams,
  facets,
  initialFacets,
  loading,
  totalHitsText,
  filterList,
  query,
  sortOptions,
  onFilterUpdate,
}: SearchFiltersProps) => {
  const [hideQuery, setHideQuery] = useState(false);
  useEffect(() => {
    if (hideQuery) {
      setHideQuery(false);
    }
  }, [facets]);

  const [openFilter, setOpenFilterId] = useState(false);
  const [openSort, setOpenSort] = useState(false);

  const onOpenFilter = useCallback(() => setOpenFilterId(true), []);
  const onCloseFilter = useCallback(() => setOpenFilterId(false), []);

  const onOpenSort = useCallback(() => setOpenSort(true), []);
  const onCloseSort = useCallback(() => setOpenSort(false), []);

  const filteredFacets = filterFacets(facets, filterList);
  const filteredInitialFacets = filterFacets(initialFacets, filterList);

  const numberOfFiltersSelected = facets?.reduce(
    (prev, curr) => {
      prev += isSelected(curr, urlSearchParams) ? 1 : 0;
      return prev;
    },
    query ? 1 : 0
  );

  if (!filteredFacets) return null;

  return (
    <>
      {openFilter && (
        <FilterPanel
          facets={filteredFacets}
          query={query}
          queryString={urlSearchParams?.toString()}
          loading={loading}
          initialFacets={filteredInitialFacets}
          urlSearchParams={urlSearchParams}
          totalHitsText={totalHitsText}
          onCloseFilter={onCloseFilter}
          onFilterUpdate={onFilterUpdate}
        />
      )}
      {openSort && sortOptions && (
        <SortPanel
          loading={loading}
          sortOptions={sortOptions}
          urlSearchParams={urlSearchParams}
          onCloseFilter={onCloseSort}
          onFilterUpdate={onFilterUpdate}
        />
      )}
      <Columns $spacing={1} valign="center">
        <Column noShrink>
          <ButtonPrimary
            type="button"
            onClick={() => onOpenFilter()}
            aria-haspopup="dialog"
            aria-expanded={openFilter}
            title={i18next.t('search.filterButton.title') || ''}
          >
            {i18next.t('search.filterButton.text')}
            {numberOfFiltersSelected ? ` (${numberOfFiltersSelected})` : ''}
          </ButtonPrimary>
        </Column>
        {sortOptions && (
          <Column noShrink>
            <ButtonSecondary
              type="button"
              onClick={() => onOpenSort()}
              aria-haspopup="dialog"
              aria-expanded={openSort}
              title={i18next.t('search.sortButton.title') || ''}
            >
              {i18next.t('search.sortButton.text')}
            </ButtonSecondary>
          </Column>
        )}
      </Columns>
    </>
  );
};

export default SearchFilters;
